import React, { useContext, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { canAccessMarketDataTools } from '../../utils/brokerConnection';
import { useBarsFetcher } from '../../hooks/useTradeStationQueries';
import LoadingSpinner from '../shared/LoadingSpinner';
import OverviewCardHeader from '../shared/OverviewCardHeader';
import {
  closesMapFromBars,
  mergeBenchmarkPctIntoCurve,
  mergeDrawdownIntoCurve,
  pctFromBaseline as pctFromEquityBaselineUtil,
  shiftDateString,
} from '../../utils/benchmarkEquitySeries';
import PerformanceTimeframeBar from './PerformanceTimeframeBar';
import EquityCurvePlot from './EquityCurvePlot';
import { applyTransferAdjustedPnl } from '../../utils/transferAdjustedEquity';
import { useAccountTransfers } from '../../hooks/useAccountTransfers';
import OutlinePrimaryButton from '../shared/OutlinePrimaryButton';

const pctFromEquityBaseline = (equity, baseline) => pctFromEquityBaselineUtil(equity, baseline);

const formatRangeDate = (str) => {
  if (!str) return '';
  const d = new Date(`${str}T12:00:00`);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const formatSignedCurrency = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n >= 0 ? `+$${abs}` : `-$${abs}`;
};

const ChartModeToggle = ({ mode, onChange, colors }) => (
  <div
    style={{
      display: 'inline-flex',
      flexShrink: 0,
      borderRadius: '6px',
      border: `1px solid ${colors.border}`,
      overflow: 'hidden',
      fontSize: '0.65rem',
    }}
  >
    {[
      { id: 'value', label: 'Account Value' },
      { id: 'pnl', label: 'P&L%' },
    ].map(({ id, label }) => {
      const active = mode === id;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          style={{
            padding: '3px 7px',
            lineHeight: 1.2,
            fontSize: '0.65rem',
            border: 'none',
            cursor: 'pointer',
            background: active ? colors.primary : 'transparent',
            color: active ? '#fff' : colors.textMuted,
            fontWeight: active ? 600 : 400,
          }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const BenchmarkSelect = ({ value, onChange, colors, isMobile, disabled = false }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    style={{
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      padding: isMobile ? '4px 8px' : '6px 10px',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      background: colors.bgSecondary,
      color: colors.text,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.55 : 1,
    }}
    aria-label="Compare to index"
  >
    <option value="SPY">SPY</option>
    <option value="QQQ">QQQ</option>
  </select>
);

/**
 * Daily account snapshot equity curve (P&L% vs account value + optional index comparison).
 * Ported from feat/trade-records-and-trade-events PerformanceDashboard.
 *
 * todayPnLPercent / todayPnL / todayValue: live values from the balance card; used when the equity
 * series has only one snapshot (new account / first day synced) so the chart shows meaningful data
 * rather than a flat +0.00% line.
 */
export default function AccountEquitySnapshotChart({
  equityData,
  timeframe,
  dateRange = null,
  onTimeframeChange,
  colors,
  isMobile,
  benchmark,
  onBenchmarkChange,
  compareWithIndex = true,
  onCompareWithIndexChange = null,
  isLoading = false,
  embedded = false,
  title = 'Equity Curve',
  todayPnLPercent = null,
  todayPnL = null,
  todayValue = null,
  onAddTransfer = null,
}) {
  const [chartMode, setChartMode] = useState('value');
  const [scrubPoint, setScrubPoint] = useState(null);
  const [showDrawdown, setShowDrawdown] = useState(true);
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const fetchBars = useBarsFetcher();
  const canOverlayBenchmark = canAccessMarketDataTools(user, selectedAccount);

  const rangeStart = equityData?.rangeStart;
  const rangeEnd = equityData?.rangeEnd;
  const isSinglePoint = (equityData?.equityCurve?.length ?? 0) <= 1;
  const serverHasBenchmark = equityData?.equityCurve?.some((p) => typeof p.benchmarkPct === 'number');
  const needsClientBenchmark =
    compareWithIndex &&
    chartMode === 'pnl' &&
    !!rangeStart &&
    !!rangeEnd &&
    !serverHasBenchmark &&
    canOverlayBenchmark;

  const accountId =
    selectedAccount?.AccountID != null ? String(selectedAccount.AccountID) : undefined;
  const paperTrading = selectedAccount?.isPaperTrading;
  const { data: transfers = [] } = useAccountTransfers({
    accountId,
    paperTrading,
    since: rangeStart || null,
    until: rangeEnd || null,
    enabled: !!accountId,
  });

  const benchmarkBarsQuery = useQuery({
    queryKey: ['equity-benchmark-bars', benchmark, rangeStart, rangeEnd, selectedAccount?.AccountID],
    queryFn: async () => {
      const res = await fetchBars({
        ticker: benchmark,
        interval: 1,
        unit: 'Daily',
        sessionTemplate: 'Default',
        firstdate: shiftDateString(rangeStart, -14),
        lastdate: rangeEnd,
      });
      if (res?.Error && !res?.Bars?.length) {
        throw new Error(typeof res.Message === 'string' ? res.Message : 'Benchmark bars unavailable');
      }
      return res;
    },
    enabled: !!user?.token && needsClientBenchmark && !!benchmark,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const chartData = useMemo(() => {
    const curve = equityData?.equityCurve ?? [];
    if (!curve.length) return [];

    const baselineEquity =
      curve.find((p) => p.equity > 0)?.equity ?? curve[0]?.equity ?? null;

    let rows = curve.map((p) => {
      let pnlPct =
        typeof p.pnlPct === 'number'
          ? p.pnlPct
          : pctFromEquityBaseline(p.equity, baselineEquity);

      // Single-point series can't compute meaningful change — use live balance P&L%.
      if (isSinglePoint && todayPnLPercent != null) {
        pnlPct = todayPnLPercent;
      }

      let benchmarkPct = p.benchmarkPct;
      if (benchmarkPct == null && typeof p.spyPct === 'number') benchmarkPct = p.spyPct;
      if (benchmarkPct == null && typeof p.qqqPct === 'number') benchmarkPct = p.qqqPct;

      return {
        ...p,
        pnlPct,
        benchmarkPct: typeof benchmarkPct === 'number' ? benchmarkPct : null,
      };
    });

    if (needsClientBenchmark && benchmarkBarsQuery.data?.Bars?.length && rangeStart) {
      const closeByDate = closesMapFromBars(benchmarkBarsQuery.data.Bars);
      rows = mergeBenchmarkPctIntoCurve(rows, closeByDate, rangeStart);
    }

    const { curve: adjustedRows } = applyTransferAdjustedPnl(rows, transfers, {
      rangeStart,
      rangeEnd,
    });

    const withLiveSinglePoint =
      isSinglePoint && todayPnLPercent != null
        ? adjustedRows.map((row) => ({
            ...row,
            pnlPct: todayPnLPercent,
            dayPnlPct: todayPnLPercent,
          }))
        : adjustedRows;

    return mergeDrawdownIntoCurve(withLiveSinglePoint, equityData?.drawdownCurve);
  }, [
    equityData,
    isSinglePoint,
    todayPnLPercent,
    needsClientBenchmark,
    benchmarkBarsQuery.data,
    rangeStart,
    rangeEnd,
    transfers,
  ]);

  const lastPoint = chartData.length ? chartData[chartData.length - 1] : null;
  const isPnlMode = chartMode === 'pnl';
  const benchmarkSymbol = equityData?.benchmark || benchmark || 'SPY';
  const benchmarkAvailable =
    compareWithIndex && chartData.some((p) => typeof p.benchmarkPct === 'number');
  const benchmarkLoading = needsClientBenchmark && benchmarkBarsQuery.isLoading;

  const rangeLabel =
    equityData?.rangeStart && equityData?.rangeEnd
      ? equityData.rangeStart === equityData.rangeEnd
        ? 'Today'
        : `Range: ${formatRangeDate(equityData.rangeStart)} – ${formatRangeDate(equityData.rangeEnd)}`
      : null;

  // For single-point series, prefer live balance values over the stale computed 0%.
  const isSingleRendered = chartData.length <= 1;
  const livePnlPct = isSingleRendered && todayPnLPercent != null
    ? todayPnLPercent
    : null;
  const liveEquity = isSingleRendered && todayValue != null
    ? todayValue
    : null;

  const baselineAdjustedEquity = useMemo(() => {
    if (!chartData.length) return null;
    return (
      chartData.find((p) => p.adjustedEquity != null && p.adjustedEquity > 0)?.adjustedEquity ??
      chartData.find((p) => p.equity > 0)?.equity ??
      chartData[0]?.equity ??
      null
    );
  }, [chartData]);

  const focusPoint = scrubPoint || lastPoint;
  const scrubDateLabel = scrubPoint?.time
    ? new Date(`${scrubPoint.time}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  const headlinePct = isPnlMode
    ? (scrubPoint?.pnlPct ?? livePnlPct ?? lastPoint?.pnlPct ?? equityData?.accountPnlPct)
    : null;
  const headlineEquity = !isPnlMode
    ? (scrubPoint?.equity ?? liveEquity ?? lastPoint?.equity)
    : null;
  const headlinePnlAmount = useMemo(() => {
    if (!isPnlMode) return null;
    if (scrubPoint) {
      const scrubAdjusted = scrubPoint.adjustedEquity != null ? scrubPoint.adjustedEquity : scrubPoint.equity;
      if (scrubAdjusted == null || baselineAdjustedEquity == null) return null;
      return scrubAdjusted - baselineAdjustedEquity;
    }
    if (isSingleRendered && todayPnL != null && Number.isFinite(Number(todayPnL))) {
      return Number(todayPnL);
    }
    const endAdjusted =
      lastPoint?.adjustedEquity != null
        ? lastPoint.adjustedEquity
        : (liveEquity ?? lastPoint?.equity);
    if (endAdjusted == null || baselineAdjustedEquity == null) return null;
    return endAdjusted - baselineAdjustedEquity;
  }, [
    isPnlMode,
    scrubPoint,
    isSingleRendered,
    todayPnL,
    liveEquity,
    lastPoint,
    baselineAdjustedEquity,
  ]);

  const headlineColor =
    (isPnlMode ? headlinePct : headlineEquity) == null
      ? colors.text
      : isPnlMode
        ? (headlinePct >= 0 ? colors.chartUp : colors.chartDown)
        : colors.text;

  const showBenchmark = compareWithIndex && canOverlayBenchmark && benchmarkAvailable;

  const cardPadding = embedded
    ? (isMobile ? '12px 14px' : '14px 18px')
    : (isMobile ? '16px 8px' : '20px');

  const cardStyle = {
    background: colors.bgCard,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: embedded ? 12 : 10,
    boxShadow: embedded ? colors.cardShadow : undefined,
    padding: cardPadding,
    marginBottom: embedded ? 0 : 24,
    width: embedded ? '100%' : undefined,
    minWidth: embedded ? 0 : undefined,
    height: embedded ? '100%' : undefined,
    boxSizing: 'border-box',
    display: embedded ? 'flex' : undefined,
    flexDirection: embedded ? 'column' : undefined,
  };

  const timeframeFooter = rangeLabel ? (
    <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>{rangeLabel}</span>
  ) : null;

  const cardHeader = embedded ? (
    <OverviewCardHeader
      title={title}
      colors={colors}
      trailing={
        onTimeframeChange ? (
          // The range label goes under the whole cluster, not under the timeframe chip —
          // as the chip's footer it was wider than the chip itself, and right-aligning
          // inside that width left a gap between Transfers and the chip.
          <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {typeof onAddTransfer === 'function' && (
                <OutlinePrimaryButton
                  size="sm"
                  onClick={onAddTransfer}
                  title="View and record deposits and withdrawals"
                  style={{
                    height: 32,
                    minHeight: 32,
                    paddingTop: 0,
                    paddingBottom: 0,
                    borderRadius: 8,
                    flexShrink: 0,
                  }}
                  data-testid="add-transfer-button"
                >
                  Transfers
                </OutlinePrimaryButton>
              )}
              <PerformanceTimeframeBar
                timeframe={timeframe}
                dateRange={dateRange}
                onChange={onTimeframeChange}
                colors={colors}
                isMobile={isMobile}
                align="right"
                compact
                desktopChip
              />
            </div>
            {timeframeFooter}
          </div>
        ) : null
      }
    />
  ) : null;

  if (isLoading && !chartData.length) {
    return (
      <div style={{ ...cardStyle, minHeight: 120 }}>
        {cardHeader}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner size="sm" />
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div style={cardStyle}>
        {cardHeader}
        <div style={{ fontSize: '0.875rem', color: colors.textMuted, flex: embedded ? 1 : undefined }}>
          Equity curve will appear once daily account snapshots are recorded.
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {cardHeader}
      <div
        style={{
          marginBottom: 12,
          flexShrink: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {isPnlMode && (headlinePnlAmount != null || headlinePct != null) && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: '2px 8px',
                lineHeight: 1.15,
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
                {headlinePnlAmount != null && (
                  <span style={{ fontSize: isMobile ? '1.35rem' : '1.75rem', fontWeight: 700, color: headlineColor, fontVariantNumeric: 'tabular-nums' }}>
                    {formatSignedCurrency(headlinePnlAmount)}
                  </span>
                )}
                {headlinePct != null && (
                  <span style={{ fontSize: isMobile ? '1.05rem' : '1.2rem', fontWeight: 700, color: headlineColor, fontVariantNumeric: 'tabular-nums' }}>
                    {headlinePct >= 0 ? '+' : ''}
                    {headlinePct.toFixed(2)}%
                  </span>
                )}
              </span>
              {scrubDateLabel && (
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted }}>{scrubDateLabel}</span>
              )}
            </div>
          )}
          {!isPnlMode && headlineEquity != null && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, lineHeight: 1.1 }}>
              <span style={{ fontSize: isMobile ? '1.5rem' : '1.75rem', fontWeight: 700, color: colors.text, fontVariantNumeric: 'tabular-nums' }}>
                ${Number(headlineEquity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {scrubDateLabel && (
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted }}>{scrubDateLabel}</span>
              )}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
          <ChartModeToggle mode={chartMode} onChange={setChartMode} colors={colors} isMobile={isMobile} />
        </div>
      </div>

      <div style={{ width: '100%', minWidth: 0, flex: embedded ? 1 : undefined }}>
      <EquityCurvePlot
        chartData={chartData}
        mode={chartMode}
        colors={colors}
        isMobile={isMobile}
        height={embedded ? (isMobile ? 200 : 220) : (isMobile ? 220 : 260)}
        timeframe={timeframe}
        showBenchmark={showBenchmark}
        benchmarkSymbol={benchmarkSymbol}
        benchmarkEndPct={focusPoint?.benchmarkPct ?? equityData?.benchmarkPnlPct}
        showDrawdown={showDrawdown}
        onToggleDrawdown={() => setShowDrawdown((v) => !v)}
        drawdownColor={colors.chartDown}
        drawdownEnd={focusPoint?.drawdown}
        onActivePointChange={setScrubPoint}
        transfers={transfers}
        isSinglePoint={isSingleRendered}
        legendTrailing={isPnlMode && canOverlayBenchmark ? (
          <>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                minHeight: 32,
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                color: colors.textMuted,
                cursor: onCompareWithIndexChange ? 'pointer' : 'default',
                userSelect: 'none',
                margin: 0,
              }}
            >
              <input
                type="checkbox"
                checked={!!compareWithIndex}
                onChange={(e) => onCompareWithIndexChange?.(e.target.checked)}
                disabled={!onCompareWithIndexChange}
                style={{ margin: 0 }}
                aria-label="Compare with index"
              />
              Compare with index
            </label>
            {compareWithIndex && (
              <BenchmarkSelect
                value={benchmark}
                onChange={onBenchmarkChange}
                colors={colors}
                isMobile={isMobile}
              />
            )}
          </>
        ) : null}
        unavailableNote={
          isPnlMode && canOverlayBenchmark && compareWithIndex && !benchmarkAvailable && !benchmarkLoading
            ? `Index comparison unavailable — check market data access for ${benchmarkSymbol}.`
            : null
        }
      />
      </div>

    </div>
  );
}
